import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FETCH_ERROR_FALLBACK, toUserMessage } from '../lib/error-copy.js'
import {
  LOADING,
  SHARE_LINK_ACTION,
  SHARE_LINK_STATUS,
  SHARE_PERMISSION_LABEL,
} from '../lib/labels.js'
import { listShareLinks, revokeShareLink } from '../lib/share-links-api.js'
import { buildShareUrl } from '../lib/share-url.js'
import { CopyUrlButton } from './CopyUrlButton.js'

type Props = {
  projectId: string
}

export function ShareLinksList({ projectId }: Props) {
  const queryClient = useQueryClient()
  const { data, isLoading, error } = useQuery({
    queryKey: ['share-links', projectId],
    queryFn: () => listShareLinks(projectId),
  })

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeShareLink(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['share-links', projectId] })
    },
  })

  if (isLoading) return <p className="text-sm text-muted-foreground">{LOADING.fetching}</p>
  if (error) {
    const msg = toUserMessage(error, FETCH_ERROR_FALLBACK)
    return <p className="text-sm text-red-600">{msg}</p>
  }

  const items = data?.items ?? []
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">まだ共有URLは発行されていません</p>
  }

  return (
    <ul className="space-y-2">
      {items.map((link) => {
        const url = buildShareUrl(link.token, link.expiresAt)
        const isExpired = new Date(link.expiresAt).getTime() <= Date.now()
        const isRevoked = !!link.revokedAt
        const isActive = !isExpired && !isRevoked

        return (
          <li
            key={link.id}
            className="border border-border rounded-md p-3 bg-background flex items-start gap-3"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800">
                  {SHARE_PERMISSION_LABEL[link.permission]}
                </span>
                {link.emailRequired && (
                  <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-800">
                    メール入力 必須
                  </span>
                )}
                {isExpired && (
                  <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                    期限切れ
                  </span>
                )}
                {isRevoked && (
                  <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-800">
                    {SHARE_LINK_STATUS.revoked}
                  </span>
                )}
              </div>
              <div className="mt-1 text-xs text-muted-foreground">
                ファイル {link.fileIds.length} 件 · 期限{' '}
                {new Date(link.expiresAt).toLocaleString('ja-JP')}
              </div>
              <div className="mt-2 text-xs font-mono text-muted-foreground truncate">{url}</div>
            </div>
            <div className="flex flex-col gap-2 shrink-0">
              <CopyUrlButton url={url} variant="outline" size="sm" disabled={!isActive} />
              {isActive && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(SHARE_LINK_ACTION.revokeConfirm)) {
                      revokeMutation.mutate(link.id)
                    }
                  }}
                  disabled={revokeMutation.isPending}
                  className="px-3 py-1.5 border border-red-300 text-red-700 rounded-md text-xs font-medium disabled:opacity-50"
                >
                  {SHARE_LINK_ACTION.revoke}
                </button>
              )}
            </div>
          </li>
        )
      })}
    </ul>
  )
}
